import { WeightBasis } from "@api/graphql/generated/generated-types";

/**
 * Resolves the `WeightBasis` value to seed into a rule / surcharge form,
 * given the caller's preferred value (e.g. the saved rule's basis, or the
 * courier's `defaultWeightBasis`) and the courier's `dimFactor`.
 *
 * When `dimFactor` is null, dim weight is disabled on this courier and the
 * basis is forced to `ActualOnly` — `MAX_OF_ACTUAL_AND_DIM` requires a
 * non-null dim factor on the backend and the UI hides the dropdown in that
 * state (see `WeightBasisSelect`). Otherwise falls back to `preferred`, or
 * `ActualOnly` if `preferred` is null/undefined.
 */
export function effectiveWeightBasis(
  preferred: WeightBasis | null | undefined,
  dimFactor: number | null | undefined,
): WeightBasis {
  if (dimFactor == null) return WeightBasis.ActualOnly;
  return preferred ?? WeightBasis.ActualOnly;
}

/**
 * Mirrors the backend `DimWeightCalculator` (cube `pricing/domain/accounting`):
 * computes dim (dimensional) weight for a single piece or a list of pieces.
 *
 *   Per piece: dimWeight = (L * W * H) / dimFactor
 *   Per list:  sum of per-piece dim weights, skipping pieces with any
 *              missing dimension
 *
 * `dimFactor` is nullable so callers can pass `courier.dimFactor` directly
 * without gating. A null factor produces a null result (feature off).
 * A non-positive factor is out-of-band input and throws.
 *
 * Consumer-defined input shape, not a generated `PackageItem` type — any
 * object that structurally has `length`, `width`, `height` (or has them
 * nullable) flows through. This lets multiple GraphQL queries with
 * different selection sets use the same util without casting.
 */
export interface DimWeightPiece {
  length?: number | null;
  width?: number | null;
  height?: number | null;
}

export function dimWeightForPiece(
  piece: DimWeightPiece,
  dimFactor: number | null | undefined,
): number | null {
  if (dimFactor == null) return null;
  if (!Number.isFinite(dimFactor) || dimFactor <= 0) {
    throw new Error(`dimFactor must be a positive finite number, got: ${dimFactor}`);
  }
  const { length, width, height } = piece;
  if (length == null || width == null || height == null) return null;
  if (!Number.isFinite(length) || !Number.isFinite(width) || !Number.isFinite(height)) return null;
  return (length * width * height) / dimFactor;
}

export function dimWeightForPieces(
  pieces: DimWeightPiece[],
  dimFactor: number | null | undefined,
): number | null {
  if (dimFactor == null) return null;
  if (!Number.isFinite(dimFactor) || dimFactor <= 0) {
    throw new Error(`dimFactor must be a positive finite number, got: ${dimFactor}`);
  }
  if (pieces.length === 0) return null;

  const perPiece: number[] = [];
  for (const piece of pieces) {
    const dw = dimWeightForPiece(piece, dimFactor);
    if (dw != null) perPiece.push(dw);
  }
  if (perPiece.length === 0) return null;
  return perPiece.reduce((sum, n) => sum + n, 0);
}
